import { PanelComponentKeyType } from 'config/panelConfig';

export type Point3 = [number, number, number];

export type ChamberAxisSavedInfoValue = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

export type ChamberAxisBasis = {
  center: Point3;
  normal: Point3;
  right: Point3;
  up: Point3;
  extra?: number;
};

export type ChamberAxisViewportId =
  | 'Axial'
  | 'FourChamber'
  | 'ShortAxis'
  | 'TwoChamber';

export type ChamberAxisApplyViewportId = Exclude<
  ChamberAxisViewportId,
  'Axial'
>;

export const CHAMBER_AXIS_PICK_VIEWPORTS: ChamberAxisViewportId[] = [
  'Axial',
  'FourChamber',
  'ShortAxis',
  'TwoChamber',
];

export const CHAMBER_AXIS_APPLY_VIEWPORTS: ChamberAxisApplyViewportId[] = [
  'FourChamber',
  'ShortAxis',
  'TwoChamber',
];

export type ChamberAxisPickMode = 'idle' | 'pickingStart' | 'pickingEnd';

export type ChamberAxisPointKind = 'start' | 'end';

export type ChamberAxisEditorState = {
  pickMode: ChamberAxisPickMode;
  startWorld?: Point3;
  endWorld?: Point3;
  error?:
    | 'missing-points'
    | 'zero-length-vector'
    | 'missing-chamber-props'
    | 'save-failed';
  lastApplied?: {
    angleRad: number;
  };
};

export type ChamberAxisControllerModel = {
  visible: boolean;
  activePickMode: ChamberAxisPickMode;
  startWorld?: Point3;
  endWorld?: Point3;
  canCalculate: boolean;
  error?: ChamberAxisEditorState['error'];
  lastApplied?: ChamberAxisEditorState['lastApplied'];
};

export type ChamberAxisVectorState = {
  normal?: Point3;
  viewNormal?: Point3;
  guideUp?: Point3;
  guidePlaneNormal?: Point3;
};

export type ChamberAxisMarker = {
  kind: ChamberAxisPointKind;
  x: number;
  y: number;
  world: Point3;
};

const EPSILON = 1e-6;

const listeners = new Set<() => void>();
let chamberAxisState: ChamberAxisEditorState = createInitialChamberAxisState();

export function createInitialChamberAxisState(): ChamberAxisEditorState {
  return {
    pickMode: 'idle',
  };
}

export function getChamberAxisSnapshot(): ChamberAxisEditorState {
  return chamberAxisState;
}

export function subscribeChamberAxisStore(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(next: ChamberAxisEditorState) {
  chamberAxisState = next;
  listeners.forEach((listener) => listener());
}

export function resetChamberAxisState() {
  publish(createInitialChamberAxisState());
}

export function setChamberAxisPickMode(pickMode: ChamberAxisPickMode) {
  publish({
    ...chamberAxisState,
    pickMode,
    error: undefined,
  });
}

export function setChamberAxisWorldPoint(
  kind: ChamberAxisPointKind,
  world: Point3,
) {
  publish({
    ...chamberAxisState,
    pickMode: kind === 'start' ? 'pickingEnd' : 'idle',
    startWorld: kind === 'start' ? world : chamberAxisState.startWorld,
    endWorld: kind === 'start' ? undefined : world,
    error: undefined,
    lastApplied: undefined,
  });
}

export function setChamberAxisError(error: ChamberAxisEditorState['error']) {
  publish({
    ...chamberAxisState,
    error,
  });
}

export function markChamberAxisApplied(angleRad: number) {
  publish({
    ...chamberAxisState,
    pickMode: 'idle',
    startWorld: undefined,
    endWorld: undefined,
    error: undefined,
    lastApplied: { angleRad },
  });
}

export function isChamberAxisPickViewport(
  componentKey: PanelComponentKeyType,
): componentKey is ChamberAxisViewportId {
  return CHAMBER_AXIS_PICK_VIEWPORTS.includes(
    componentKey as ChamberAxisViewportId,
  );
}

export function isChamberAxisApplyViewport(
  componentKey: PanelComponentKeyType,
): componentKey is ChamberAxisApplyViewportId {
  return CHAMBER_AXIS_APPLY_VIEWPORTS.includes(
    componentKey as ChamberAxisApplyViewportId,
  );
}

export function resolveChamberAxisActiveViewport({
  selectedPanelComponentKey,
  fallback = 'FourChamber',
}: {
  selectedPanelComponentKey?: PanelComponentKeyType;
  fallback?: ChamberAxisApplyViewportId;
}): {
  guideViewportId: ChamberAxisViewportId;
  applyViewportId?: ChamberAxisApplyViewportId;
} {
  const guideViewportId =
    selectedPanelComponentKey &&
    isChamberAxisPickViewport(selectedPanelComponentKey)
      ? selectedPanelComponentKey
      : fallback;

  return {
    guideViewportId,
    applyViewportId: isChamberAxisApplyViewport(guideViewportId)
      ? guideViewportId
      : undefined,
  };
}

export function getChamberAxisControllerModel({
  analysisType,
  componentKey,
  state = chamberAxisState,
}: {
  analysisType?: string;
  componentKey: PanelComponentKeyType;
  state?: ChamberAxisEditorState;
}): ChamberAxisControllerModel {
  const visible = analysisType === 'chamber' && componentKey === 'Direction';

  return {
    visible,
    activePickMode: state.pickMode,
    startWorld: state.startWorld,
    endWorld: state.endWorld,
    canCalculate: !!state.startWorld && !!state.endWorld,
    error: state.error,
    lastApplied: state.lastApplied,
  };
}

export function projectChamberAxisMarkers({
  state,
  worldToCanvas,
}: {
  state: ChamberAxisEditorState;
  worldToCanvas: (world: Point3) => { x: number; y: number } | undefined;
}): ChamberAxisMarker[] {
  return (
    [
      ['start', state.startWorld],
      ['end', state.endWorld],
    ] as [ChamberAxisPointKind, Point3 | undefined][]
  )
    .map(([kind, world]) => {
      if (!world) return undefined;
      const canvas = worldToCanvas(world);
      if (!canvas) return undefined;
      if (!Number.isFinite(canvas.x) || !Number.isFinite(canvas.y)) {
        return undefined;
      }

      return {
        kind,
        x: canvas.x,
        y: canvas.y,
        world,
      };
    })
    .filter((marker): marker is ChamberAxisMarker => !!marker);
}

export function readChamberAxisBasis(
  value: ChamberAxisSavedInfoValue | number[] | undefined,
): ChamberAxisBasis | undefined {
  if (!value || value.length < 13) return undefined;
  if (!value.every((item) => Number.isFinite(item))) return undefined;

  return {
    normal: [value[0], value[1], -value[2]],
    center: [-value[3], -value[4], value[5]],
    right: [value[6], value[7], -value[8]],
    up: [value[9], value[10], -value[11]],
    extra: value[12],
  };
}

export function copyChamberAxisSavedInfoValue(
  value: ChamberAxisSavedInfoValue | number[] | undefined,
): ChamberAxisSavedInfoValue | undefined {
  if (!value || value.length < 13) return undefined;
  const next = value.slice(0, 13);
  if (!next.every((item) => Number.isFinite(item))) return undefined;

  return next as ChamberAxisSavedInfoValue;
}

export function getChamberAxisMidpoint(
  startWorld?: Point3,
  endWorld?: Point3,
): Point3 | undefined {
  if (!startWorld || !endWorld) return undefined;
  if (!isFinitePoint(startWorld) || !isFinitePoint(endWorld)) return undefined;

  return [
    (startWorld[0] + endWorld[0]) / 2,
    (startWorld[1] + endWorld[1]) / 2,
    (startWorld[2] + endWorld[2]) / 2,
  ];
}

export function worldToChamberAxisCenterCoordinate({
  world,
  origin,
  spacing,
  scale,
}: {
  world?: Point3;
  origin?: Point3;
  spacing?: Point3;
  scale?: number;
}): Point3 | undefined {
  if (!world || !origin || !spacing || !Number.isFinite(scale)) {
    return undefined;
  }
  if (
    !isFinitePoint(world) ||
    !isFinitePoint(origin) ||
    !isFinitePoint(spacing)
  ) {
    return undefined;
  }
  const coordinateScale = scale as number;
  if (spacing.some((value) => Math.abs(value * coordinateScale) < EPSILON)) {
    return undefined;
  }

  return world.map(
    (value, index) =>
      (value - origin[index]) / (spacing[index] * coordinateScale),
  ) as Point3;
}

export function writeChamberAxisBasis(
  basis: ChamberAxisBasis,
  previousValue?: ChamberAxisSavedInfoValue | number[],
): ChamberAxisSavedInfoValue {
  const extra =
    previousValue && Number.isFinite(previousValue[12])
      ? previousValue[12]
      : (basis.extra ?? 0);

  return [
    basis.normal[0],
    basis.normal[1],
    -basis.normal[2],
    -basis.center[0],
    -basis.center[1],
    basis.center[2],
    -basis.right[0],
    -basis.right[1],
    basis.right[2],
    basis.up[0],
    basis.up[1],
    -basis.up[2],
    extra,
  ];
}

export function calculateChamberAxisBasisRotation({
  startWorld,
  endWorld,
  basis,
}: {
  startWorld?: Point3;
  endWorld?: Point3;
  basis?: ChamberAxisBasis;
}):
  | {
      status: 'applied';
      angleRad: number;
      axis: Point3;
      targetLongAxis: Point3;
      basis: ChamberAxisBasis;
    }
  | {
      status: 'invalid';
      reason: 'missing-points' | 'zero-length-vector' | 'missing-chamber-props';
      basis?: ChamberAxisBasis;
    } {
  if (!startWorld || !endWorld) {
    return { status: 'invalid', reason: 'missing-points', basis };
  }
  if (!basis) {
    return { status: 'invalid', reason: 'missing-chamber-props', basis };
  }

  const targetLongAxis = normalizePoint(subtractPoint(endWorld, startWorld));
  const sourceLongAxis = normalizePoint(negatePoint(basis.right));
  if (!targetLongAxis || !sourceLongAxis) {
    return { status: 'invalid', reason: 'zero-length-vector', basis };
  }

  const rotation = getShortestRotation(sourceLongAxis, targetLongAxis);
  return {
    status: 'applied',
    angleRad: rotation.angleRad,
    axis: rotation.axis,
    targetLongAxis,
    basis: {
      ...basis,
      normal: rotatePointAroundAxis(
        basis.normal,
        rotation.axis,
        rotation.angleRad,
      ),
      right: rotatePointAroundAxis(
        basis.right,
        rotation.axis,
        rotation.angleRad,
      ),
      up: rotatePointAroundAxis(basis.up, rotation.axis, rotation.angleRad),
    },
  };
}

export function calculateChamberAxisRotation<T extends ChamberAxisVectorState>({
  startWorld,
  endWorld,
  currentLongAxis,
  viewports,
}: {
  startWorld?: Point3;
  endWorld?: Point3;
  currentLongAxis?: Point3;
  viewports: Partial<Record<ChamberAxisViewportId, T>>;
}):
  | {
      status: 'applied';
      angleRad: number;
      axis: Point3;
      targetLongAxis: Point3;
      viewports: Partial<Record<ChamberAxisViewportId, T>>;
    }
  | {
      status: 'invalid';
      reason: 'missing-points' | 'zero-length-vector' | 'missing-chamber-props';
      viewports: Partial<Record<ChamberAxisViewportId, T>>;
    } {
  if (!startWorld || !endWorld) {
    return { status: 'invalid', reason: 'missing-points', viewports };
  }
  if (!currentLongAxis) {
    return { status: 'invalid', reason: 'missing-chamber-props', viewports };
  }

  const targetLongAxis = normalizePoint(subtractPoint(endWorld, startWorld));
  const sourceLongAxis = normalizePoint(currentLongAxis);
  if (!targetLongAxis || !sourceLongAxis) {
    return { status: 'invalid', reason: 'zero-length-vector', viewports };
  }

  const rotation = getShortestRotation(sourceLongAxis, targetLongAxis);
  const nextViewports = Object.fromEntries(
    Object.entries(viewports).map(([viewportId, viewport]) => [
      viewportId,
      rotateViewportVectors(viewport as T, rotation),
    ]),
  ) as Partial<Record<ChamberAxisViewportId, T>>;

  return {
    status: 'applied',
    angleRad: rotation.angleRad,
    axis: rotation.axis,
    targetLongAxis,
    viewports: nextViewports,
  };
}

function rotateViewportVectors<T extends ChamberAxisVectorState>(
  viewport: T,
  rotation: { axis: Point3; angleRad: number },
): T {
  return {
    ...viewport,
    normal: rotateOptionalPoint(viewport.normal, rotation),
    viewNormal: rotateOptionalPoint(viewport.viewNormal, rotation),
    guideUp: rotateOptionalPoint(viewport.guideUp, rotation),
    guidePlaneNormal: rotateOptionalPoint(viewport.guidePlaneNormal, rotation),
  };
}

function rotateOptionalPoint(
  point: Point3 | undefined,
  rotation: { axis: Point3; angleRad: number },
) {
  return point
    ? rotatePointAroundAxis(point, rotation.axis, rotation.angleRad)
    : point;
}

function subtractPoint(a: Point3, b: Point3): Point3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function isFinitePoint(point: Point3) {
  return point.every((value) => Number.isFinite(value));
}

function negatePoint(point: Point3): Point3 {
  return [-point[0], -point[1], -point[2]];
}

function normalizePoint(point: Point3): Point3 | undefined {
  const magnitude = Math.hypot(point[0], point[1], point[2]);
  if (!Number.isFinite(magnitude) || magnitude < EPSILON) return undefined;

  return [point[0] / magnitude, point[1] / magnitude, point[2] / magnitude];
}

function getShortestRotation(source: Point3, target: Point3) {
  const clampedDot = Math.min(1, Math.max(-1, dotPoint(source, target)));
  if (clampedDot > 1 - EPSILON) {
    return {
      axis: [1, 0, 0] as Point3,
      angleRad: 0,
    };
  }

  if (clampedDot < -1 + EPSILON) {
    return {
      axis: getStableOrthogonalAxis(source),
      angleRad: Math.PI,
    };
  }

  const axis = normalizePoint(crossPoint(source, target));
  return {
    axis: axis ?? ([1, 0, 0] as Point3),
    angleRad: Math.acos(clampedDot),
  };
}

function getStableOrthogonalAxis(point: Point3): Point3 {
  const candidate =
    Math.abs(point[0]) < 0.9
      ? crossPoint(point, [1, 0, 0])
      : crossPoint(point, [0, 1, 0]);

  return normalizePoint(candidate) ?? [0, 0, 1];
}

function rotatePointAroundAxis(point: Point3, axis: Point3, angleRad: number) {
  if (Math.abs(angleRad) < EPSILON) return normalizePoint(point) ?? point;

  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const axisDotPoint = dotPoint(axis, point);
  const axisCrossPoint = crossPoint(axis, point);
  const rotated: Point3 = [
    point[0] * cos +
      axisCrossPoint[0] * sin +
      axis[0] * axisDotPoint * (1 - cos),
    point[1] * cos +
      axisCrossPoint[1] * sin +
      axis[1] * axisDotPoint * (1 - cos),
    point[2] * cos +
      axisCrossPoint[2] * sin +
      axis[2] * axisDotPoint * (1 - cos),
  ];

  return normalizePoint(rotated) ?? rotated;
}

function dotPoint(a: Point3, b: Point3) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function crossPoint(a: Point3, b: Point3): Point3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}
